import { ItemRenderProps, TreeView, TreeViewCheckChangeEvent, TreeViewExpandChangeEvent, handleTreeViewCheckChange, processTreeViewItems } from "@progress/kendo-react-treeview";
import { useCallback, useEffect, useState } from "react";
import { clone } from "../../commons/helpers/ObjectHelper";
import CustomItemRender from "./CustomItemRender";
import { Label } from "@progress/kendo-react-labels";
import "./customtreeview.css";
import { setFormModified, setTacheToUpdate } from "../../redux/slices/appStateSlice";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import showConfirmationModal from "../../commons/helpers/ConfirmationModal";
import { approbationWebAPI } from "../../redux/api/cache";

interface CustomTreeViewProps {
    data: any[];
    resetAllQuery: any;
    children: any;
    name: string;
    check: any;
    setCheck: React.Dispatch<any>;
    resetOtherTabs: () => void;
    resetData?: (tacheId: number) => void
}

export default function CustomTreeView(props: CustomTreeViewProps) {
    const dispatch = useAppDispatch();
    const formModified = useAppSelector(state => state.appState.formModified);
    const tacheToUpdate = useAppSelector(state => state.appState.tacheToUpdate);
    const [filteredData, setFilteredData] = useState<any[]>(clone(props.data));
    useEffect(() => {
        setFilteredData(clone(props.data));
    }, [props.data])

    useEffect(() => {
        if (tacheToUpdate !== -1 && props.name === "taches") {
            let clonedData = clone(filteredData);
            let currentTache = clonedData.find((c: any) => c.id === tacheToUpdate);
            if (currentTache.produits === 1)
                props.children[0].update();
            currentTache.produits = currentTache.produits - 1;
            setFilteredData(clonedData);
            dispatch(setTacheToUpdate(-1));
        }
    }, [tacheToUpdate])

    const search = (items: any[], term: string) => {
        return items.reduce((acc: any[], item: any) => {
            if (item.text.toLowerCase().includes(term.toLowerCase()))
                acc.push(item);
            else if (item.children && item.children.length > 0) {
                let newItems = search(item.children, term);
                if (newItems && newItems.length > 0)
                    acc.push({ ...item, children: newItems });
            }
            return acc;
        }, []);
    }

    const handleSearch = (event: any) => {
        let data = search(props.data.map(d => {
            let existingData = filteredData.find(f => (f.four_cd && (f.four_cd === d.four_cd)) || (f.div_num && (f.div_num === d.div_num)));
            return existingData ? existingData : d
        }), event.target.value);
        setFilteredData(clone(data));
    };

    const getQueryParam = useCallback((event: any) => {
        let item = event.item;
        if (item.four_cd)
            return { four: item.four_cd, div: -1, dept: "" };
        else if (item.div_num)
            return item.div_num;
        else if (item.dept_cd)
            return { four: "", div: filteredData[Number(event.itemHierarchicalIndex.split("_")[0])].div_num, dept: item.dept_cd };
        else
            return { tacheId: item.id };
    }, [filteredData]);

    const onExpandChange = useCallback(async (event: TreeViewExpandChangeEvent) => {
        let copyData = clone(filteredData);
        let levels = event.itemHierarchicalIndex.split("_");
        let lastLevel = levels[levels.length - 1];
        let current = copyData;
        for (let i = 0; i < levels.length; i++) {
            if (i === levels.length - 1)
                break;

            current = current[levels[i]].children;
        }
        current[lastLevel].expanded = !current[lastLevel].expanded;
        setFilteredData(copyData);
        let result = await props.children[event.itemHierarchicalIndex.split("_").length - 1].fetch(getQueryParam(event), true).unwrap();
        if (result.length === 0)
            current[lastLevel].hasChildren = false;
        current[lastLevel].children = result;
        setFilteredData(clone(copyData));
    }, [filteredData, props, getQueryParam]);

    const onCheckChange = useCallback((event: TreeViewCheckChangeEvent) => {
        if (formModified === 0 || showConfirmationModal()) {
            props.resetOtherTabs();
            dispatch(setFormModified(0));
            props.resetAllQuery();
            let levels = event.itemHierarchicalIndex.split("_");
            let copyData = clone(filteredData);
            for (let i = 0; i < levels.length; i++) {
                props.children[i].update(copyData[levels[i]]);
                copyData = copyData[levels[i]].children;
            }
            props.setCheck(handleTreeViewCheckChange(event, props.check, filteredData, { checkParents: true, checkChildren: true, singleMode: true }, "children"));
        }
    }, [filteredData, props.check, props, formModified, dispatch]);

    const invalidateTab = () => {
        if (props.name === "taches" || props.name === "fournisseurs" || props.name === "divisions" || props.name === "activites")
            dispatch(approbationWebAPI.util.invalidateTags([props.name]));
    };

    const CustomItemRenderWrapper = (defaultProps: ItemRenderProps) => {
        return <CustomItemRender {...defaultProps} resetData={props.resetData} />
    };

    return <>
        <div className="k-d-flex treeview-header-wrapper">
            <input className="k-textbox treeview-search-input" onChange={handleSearch} placeholder="Rechercher" />
            <span className="k-icon k-font-icon k-i-refresh k-icon-lg" style={{ cursor: 'pointer', marginTop: '10px', marginLeft: '5px' }} onClick={() => { invalidateTab() }} />
            <div className="k-d-flex k-justify-content-end k-w-full treeview-wrapper-text">
                <Label className="treeview-sideway-text">Produits</Label>
                <Label className="treeview-sideway-text">{props.name === "taches" ? "Actions" : "Approuvés"}</Label>
            </div>
        </div>
        <TreeView data={processTreeViewItems(filteredData, { check: props.check, childrenField: 'children' })} expandIcons={true} checkboxes={true} animate={true} size={"small"}
            textField="text" onExpandChange={onExpandChange} onCheckChange={onCheckChange} onItemClick={onCheckChange} childrenField="children" item={CustomItemRenderWrapper} /></>
};